import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  signInWithCredential,
  signInWithEmailAndPassword,
  getRedirectResult,
  onIdTokenChanged,
  signOut as firebaseSignOut,
  updateProfile,
} from "firebase/auth";

import { authStore } from "@/store/auth.js";
import { clearAdminUsersCache } from "@/store/adminUsers.js";
import router from "@/router/index";
import { getDatabase, ref, runTransaction } from "firebase/database";
import { landingRoute } from "@/scripts/landingRoute.js";
import { usePushNotifications } from "@/composables/usePushNotifications.js";
import { signInModal } from "@/store/signInModal.js";
import { providerNameStore } from "@/store/providerName.js";
import { showErrorToast } from "@/scripts/friendlyError.js";
import { readAcquisitionForSeed } from "@/scripts/acquisition.js";
import { STORAGE_KEYS } from "@/constants/storageKeys.js";

const isCapacitor = () => window.Capacitor?.isNativePlatform?.() === true;

// Mirrors the enum in database.rules.json for users/$uid/signupPlatform.
const SIGNUP_PLATFORMS = ["web", "ios", "android"];

// The RTDB rule accepts exactly these three values, so anything else must be
// normalised away before it reaches the seed write — a rejected payload would
// leave the account with no profile at all rather than just an unknown
// platform. @capacitor/core's web stub returns "web" on web builds, so the
// fallback only covers pages loaded without the Capacitor bundle, a bridge
// that throws, or a platform Capacitor adds later.
const resolveSignupPlatform = () => {
  let platform;
  try {
    platform = window.Capacitor?.getPlatform?.();
  } catch (error) {
    console.warn("Could not read the Capacitor platform", error);
  }
  return SIGNUP_PLATFORMS.includes(platform) ? platform : "web";
};

// Survives the full-page reload of the web signInWithRedirect flow so the
// destination chosen at click time can be honoured on return (see
// startAuth and completePendingRedirectAuth).
const PENDING_AUTH_REDIRECT_KEY = "articulearn.pendingAuthRedirect";

// What is stashed for the mobile full-page redirect when the visitor had no
// explicit ?redirect= to honour.
//
// It is a marker rather than a route because the default destination cannot be
// decided at click time. Which route that is depends on the `dailyPlay` flag
// for the account being signed INTO, and the flag store has not yet subscribed
// to that account's allowlist entry when the button is tapped; the account is
// not even known. Storing a route here would freeze that answer at the one
// moment it cannot be right. The marker defers the decision to
// completePendingRedirectAuth, which resolves it after the auth state reports.
//
// It is deliberately not a legal path, so a value read back from an older
// session's sessionStorage can never be pushed as one.
const DEFAULT_LANDING_MARKER = "__default__";

/**
 * Land on the default destination, unless the visitor has moved on meanwhile.
 *
 * `landingRoute()` waits up to LANDING_FLAG_WAIT_MS for the flag store to
 * report, and these two callers fire from an auth listener rather than from a
 * tap. Both used to push synchronously, so there was no window at all; now
 * there is one, and on a public marketing page a signed-in visitor can read and
 * click inside it. Pushing regardless would cancel a navigation they chose
 * themselves, which is worse than landing a beat late.
 *
 * The guard is the route they were on when the listener fired: if it is still
 * the current one, nothing of theirs is being overridden.
 */
function pushWhenStillWaiting(targetRouter) {
  const from = targetRouter.currentRoute.value.fullPath;
  landingRoute()
    .then((target) => {
      if (targetRouter.currentRoute.value.fullPath !== from) return;
      return targetRouter.push(target);
    })
    .catch((error) => console.error("[auth] could not land after sign-in", error));
}

// The destination an explicit ?redirect= asks for, or null for the default.
//
// The router guard sets that query value when it bounces an unauthenticated
// visitor off a protected route, and it is honoured unchanged on every path
// that accepts it. But the value arrives from the URL, so it is narrowed to the
// shape the guard actually writes: ONE in-app path, beginning with a single
// slash. Vue Router hands back an ARRAY when a parameter appears twice, which
// would otherwise be stored and later pushed as a comma-joined string, and a
// leading "//" or an absolute URL would send the visitor off the site entirely
// after they signed in.
const explicitRedirect = () => {
  const value = router.currentRoute.value.query.redirect;
  if (typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
};

// The uids this page has already issued a seed for. The seed runs on every
// explicit sign-in and from the pending-seed recovery below, and both can name
// an account whose seed is already in flight or done: an account signed out of
// and back into in one session, or a recovery that runs while this page's own
// seed has not settled yet. Without this, each of those re-ran the seed
// transaction, which on a cold RTDB cache sees null locally, opens a write, and
// then aborts once the server sends the real node. A set rather than a single
// uid so switching accounts back and forth in one session does not re-seed an
// account already handled. An entry is dropped when its transaction fails, so
// the next explicit sign-in of that account in this session tries again.
const seededUids = new Set();

// The pending-seed marker (STORAGE_KEYS.PENDING_PROFILE_SEED) records a seed
// that was issued and has not settled, so a seed a reload or closed tab cut
// off is finished on the next page load. Every storage access is wrapped: in a
// private window or with storage blocked the marker is simply absent, which
// only means an interrupted seed is not recovered.
const PENDING_SEED_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function writePendingSeedMarker(uid, now) {
  try {
    window.localStorage.setItem(
      STORAGE_KEYS.PENDING_PROFILE_SEED,
      JSON.stringify({ uid, at: now })
    );
  } catch {
    // Storage unavailable. An interrupted seed will not be recovered.
  }
}

// The stored marker text, or null when there is none or storage fails.
function readPendingSeedMarkerText() {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.PENDING_PROFILE_SEED);
  } catch {
    return null;
  }
}

function removePendingSeedMarker() {
  try {
    window.localStorage.removeItem(STORAGE_KEYS.PENDING_PROFILE_SEED);
  } catch {
    // Storage unavailable; there is nothing to remove from it either.
  }
}

// The marker as `{uid, at}` when it is usable at `now`, or null. Usable means
// it parses as a plain object whose uid is a non-empty string and whose `at`
// is a finite number from 0 to 24 hours before `now`. A marker from the future
// is refused rather than treated as fresh, so a device clock corrected
// backward cannot keep one alive.
function parsePendingSeedMarker(text, now) {
  if (typeof text !== "string") return null;
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  if (typeof parsed.uid !== "string" || parsed.uid === "") return null;
  if (!Number.isFinite(parsed.at)) return null;
  const age = now - parsed.at;
  if (age < 0 || age > PENDING_SEED_MAX_AGE_MS) return null;
  return { uid: parsed.uid, at: parsed.at };
}

// Removes the marker only while it still names `uid`, so a seed settling late
// for one account never deletes the marker another account's seed wrote since.
function clearPendingSeedMarkerFor(uid) {
  const text = readPendingSeedMarkerText();
  if (text === null) return;
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return;
  }
  if (parsed && parsed.uid === uid) removePendingSeedMarker();
}

/**
 * Finishes a seed that a reload or closed tab interrupted. Called by the three
 * places that learn who is signed in after a page load: waitForAuthInitialization,
 * setupAuth and setupAuthPresence. Never throws, so waitForAuthInitialization
 * still resolves when storage or the observer fails.
 *
 * The signed-in user is taken from a one-shot onIdTokenChanged callback, never
 * from getAuth().currentUser. Auth sets currentUser before it has run the
 * initialization batch of observer callbacks, and the database's own token
 * listener is one of them, so a seed taken from currentUser could reach the
 * socket ahead of the new token again. Inside the callback the seed is deferred
 * with queueMicrotask: Auth queues the whole batch before running any of it,
 * so a microtask queued from one callback runs after the rest of the batch,
 * including a database listener registered after this one. A timer would do
 * the same, but browsers stretch timers to about a second in hidden tabs, and
 * the dashboard's timezone write, which comes from a websocket event, is not
 * delayed.
 */
function recoverInterruptedSeed(auth) {
  try {
    const text = readPendingSeedMarkerText();
    if (text === null) return;
    const marker = parsePendingSeedMarker(text, Date.now());
    if (marker === null) {
      removePendingSeedMarker();
      return;
    }
    let handled = false;
    let unsubscribe = null;
    unsubscribe = onIdTokenChanged(auth, (user) => {
      if (handled) return;
      handled = true;
      if (typeof unsubscribe === "function") unsubscribe();
      if (!user || user.uid !== marker.uid) {
        // Nobody signed in, or another account: this marker can never be
        // finished by the account that is here, so it goes.
        clearPendingSeedMarkerFor(marker.uid);
        return;
      }
      queueMicrotask(() => {
        // The seed this marker recorded may have settled meanwhile on this
        // same page (it removes the marker when it does), and a refused seed
        // must not be retried from here.
        const current = parsePendingSeedMarker(
          readPendingSeedMarkerText(),
          Date.now()
        );
        if (current === null || current.uid !== user.uid) return;
        issueSeed(user);
      });
    });
    // Covers an observer that reported synchronously, before `unsubscribe`
    // was assigned. Firebase reports asynchronously, but one-shot must hold.
    if (handled && typeof unsubscribe === "function") unsubscribe();
  } catch (error) {
    console.error("[auth] could not check for an interrupted profile seed", error);
  }
}

/**
 * Issues the first-sign-in seed for `user` and returns at once. Every caller is
 * a place where a sign-in has just completed, and none of them waits for the
 * transaction: navigation proceeds while it is in flight. A synchronous throw
 * from the database SDK is logged rather than allowed to turn a completed
 * sign-in into an error toast. The seed had already claimed the uid in
 * seededUids by then, and its own .catch (which releases the claim) never got
 * attached, so the claim is released here, letting the next explicit sign-in
 * of this account in this session try again. The marker stays, so the next
 * page load can also finish it.
 */
function issueSeed(user) {
  if (!user || typeof user.uid !== "string" || user.uid === "") return;
  try {
    void authUtils.seedUserProfileIfMissing(user);
  } catch (error) {
    seededUids.delete(user.uid);
    console.error("Could not seed the user profile", error);
  }
}

// Sign-out is coalesced onto this one promise, not merely de-duplicated, and
// the difference matters twice over.
//
// The problem: on native each invocation awaits FCM token deletion and the
// native plugin's own sign-out, so two of them (an ordinary double tap) can be
// seconds apart. The first finishes and routes to the marketing home page,
// where the logged-out language switcher is live; a visitor pick made there
// would then be erased by the second invocation's cleanup.
//
// Why not the shared singleFlight helper: it DROPS a concurrent call, which is
// right for a tap handler but wrong here. useAccountDeletion awaits this and
// then reports success to the user, so a dropped call would resolve instantly
// while auth teardown was still running. Handing every caller the in-flight
// promise gives them the real completion and still runs the cleanup once.
let signOutInFlight = null;

async function runSignOut() {
  // Disassociate this device's FCM token from the current user before auth
  // is torn down, so the next person to sign in here does not inherit their
  // push nudges. Guarded by platform, not the mobileNotifications flag: the
  // runtime flag may not have resolved yet at sign-out, and deleteToken()
  // no-ops when push was never initialized.
  if (isCapacitor()) {
    try {
      await usePushNotifications().deleteToken();
    } catch (err) {
      console.error("[auth] FCM token cleanup on signOut failed", err);
    }
  }
  // Native plugin must be signed out first on Capacitor — otherwise its
  // persisted keychain entry survives, leaving native auth state out of
  // sync with the web SDK and risking unexpected re-auth on next launch.
  if (isCapacitor()) {
    try {
      const plugin = window.Capacitor.Plugins.FirebaseAuthentication;
      await plugin.signOut();
    } catch (err) {
      console.error("[auth] native signOut failed", err);
    }
  }
  try {
    await firebaseSignOut(getAuth());
  } catch (err) {
    console.error("[auth] web signOut failed", err);
  }
  authStore.setUserId(null);
  authStore.setIdToken(null);
  // The next person on this browser must not see or inherit the previous
  // account's language. Clears the in-memory value and the preferredLocale
  // cache, leaving the live locale alone; see authStore.clearNativeLanguage.
  authStore.clearNativeLanguage();
  // The same for the playback speed the chip cached on this device.
  authStore.clearPlaybackRate();
  providerNameStore.clear();
  clearAdminUsersCache();
  // A seed marker left by this account must never be read after another
  // account signs in on this browser.
  removePendingSeedMarker();
  router.push("/");
}

export const authUtils = {
  waitForAuthInitialization: async () => {
    const auth = getAuth();
    // Registers its own one-shot observer when a marker is stored, on both
    // branches below, and never reads the currentUser the fast path uses.
    recoverInterruptedSeed(auth);
    if (auth.currentUser) {
      authStore.setUserId(auth.currentUser.uid);
      auth.currentUser.getIdToken().then((token) => authStore.setIdToken(token));
      return;
    }
    // Wait for the JS SDK's persistence layer (localStorage on Capacitor,
    // IndexedDB on web) to finish reading. The listener fires once with the
    // resolved state — populate authStore so the router guard can act on it.
    return new Promise((resolve) => {
      const unsubscribe = onIdTokenChanged(auth, (user) => {
        unsubscribe();
        if (user) {
          authStore.setUserId(user.uid);
          user.getIdToken().then((token) => authStore.setIdToken(token));
        }
        resolve();
      });
    });
  },
  // See runSignOut above for why this is coalesced rather than dropped.
  signOut: () => {
    signOutInFlight ??= runSignOut().finally(() => {
      signOutInFlight = null;
    });
    return signOutInFlight;
  },
  // Persists the name Apple handed us in the native authorization sheet.
  // Called right after signInWithCredential, so currentUser is the account the
  // name belongs to. The in-memory stash is written first: even if the
  // updateProfile network call fails, the onboarding wizard can still read the
  // name this session (Guideline 4: never re-ask for it). Failure is
  // deliberately non-fatal — the RTDB write in the wizard's goToStep2 is the
  // product's source of truth for the name.
  persistProviderDisplayName: async (displayName) => {
    const currentUser = getAuth().currentUser;
    if (!currentUser) return;
    providerNameStore.set(currentUser.uid, displayName);
    if (currentUser.displayName) return;
    try {
      await updateProfile(currentUser, { displayName });
    } catch (error) {
      console.error("[auth] could not save the provider display name", error);
    }
  },
  startAuth: async () => {
    // Single entry point for all CTAs; opens the provider picker rather than
    // signing in directly. The modal's buttons call signInWithProvider.
    signInModal.open();
  },
  // providerId: "google.com" | "apple.com"
  signInWithProvider: async (providerId) => {
    // Clicking "Log in" / "Get Started" is an explicit intent to enter the app,
    // so post-login navigation is owned here rather than by the page-level
    // authSetupMixin. That mixin only lives on pages that WANT to auto-redirect
    // already-logged-in visitors (Home, About, etc.) and is deliberately absent
    // from Blog/FAQ — without this, signing in from those pages left the user
    // stranded on the marketing page. Honour a pending ?redirect= (set by the
    // router guard when bouncing an unauthenticated user off a protected route).
    // null means "the default", which navigateAfterAuth resolves after sign-in
    // completes rather than here, before the account is even known.
    const destination = explicitRedirect();
    const buildWebProvider = () => {
      if (providerId === "apple.com") {
        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");
        return provider;
      }
      return new GoogleAuthProvider();
    };
    try {
      if (isCapacitor()) {
        // Use Capacitor plugin bridge directly (avoids webpack chunk loading issues)
        const plugin = window.Capacitor.Plugins.FirebaseAuthentication;
        let credential;
        // Apple's one-shot name from the native authorization sheet; the
        // identity token does not carry it, so this is the only chance to
        // keep it (re-authorizations return null).
        let appleDisplayName = null;
        if (providerId === "apple.com") {
          // skipNativeAuth is REQUIRED for Apple: the identity token is
          // single-use, so letting the native layer consume it would make the
          // web-SDK signInWithCredential below fail with invalid-credential.
          const result = await plugin.signInWithApple({ skipNativeAuth: true });
          const idToken = result?.credential?.idToken;
          const rawNonce = result?.credential?.nonce;
          if (!idToken) throw new Error("Apple Sign-In failed: no ID token received");
          appleDisplayName = result?.user?.displayName || null;
          credential = new OAuthProvider("apple.com").credential({
            idToken,
            rawNonce,
          });
        } else {
          const result = await plugin.signInWithGoogle();
          const idToken = result?.credential?.idToken;
          if (!idToken) throw new Error("Google Sign-In failed: no ID token received");
          credential = GoogleAuthProvider.credential(idToken);
        }
        const signedIn = await signInWithCredential(getAuth(), credential);
        // Before anything else is awaited, and in particular before the
        // updateProfile call below: MobileOnboardingView's setupAuth listener
        // starts navigating as soon as it sees this sign-in.
        issueSeed(signedIn?.user);
        if (appleDisplayName) {
          await authUtils.persistProviderDisplayName(appleDisplayName);
        }
        await authUtils.navigateAfterAuth(destination);
      } else if (authUtils.isMobile()) {
        // Full-page redirect: stash the destination so it survives the reload,
        // then finish navigation on return via completePendingRedirectAuth().
        // With no explicit ?redirect= the marker is stashed instead of a route,
        // so the default is resolved on return rather than now.
        sessionStorage.setItem(
          PENDING_AUTH_REDIRECT_KEY,
          destination ?? DEFAULT_LANDING_MARKER
        );
        signInWithRedirect(getAuth(), buildWebProvider());
      } else {
        const signedIn = await signInWithPopup(getAuth(), buildWebProvider());
        issueSeed(signedIn?.user);
        await authUtils.navigateAfterAuth(destination);
      }
    } catch (error) {
      // No seed here. signInWithPopup can reject (popup-closed-by-user after
      // its 8-second timer) while the sign-in goes on to complete, but at this
      // point getAuth().currentUser is empty or the account signed in before
      // the popup opened, never the one being created, so seeding it could
      // attach this sign-in's first touch to the wrong account.
      sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
      console.error("Auth error:", error);
      // showErrorToast is synchronous, so the dynamic import() that used to
      // wrap this is gone: the error path no longer needs draining in tests.
      // It also shows nothing at all when the user simply cancelled.
      showErrorToast(error, "auth");
    }
  },
  // Reviewer sign-in path (see docs/superpowers/specs/
  // 2026-07-22-email-password-review-login-design.md). Uses the web SDK
  // directly: email/password is not OAuth and the session already lives in
  // the web SDK. Unlike signInWithProvider, errors propagate to the caller —
  // SignInModal keeps itself open and renders them inline (no toast).
  // Only SignInModal's Android-native branch surfaces this, but that is a UI
  // visibility choice, NOT a security boundary: the Firebase Email/Password
  // provider is enabled project-wide, so the REST API accepts the same
  // credentials from anywhere regardless of what the client gates.
  signInWithEmail: async (email, password) => {
    const destination = explicitRedirect();
    const signedIn = await signInWithEmailAndPassword(
      getAuth(),
      email.trim(),
      password
    );
    issueSeed(signedIn?.user);
    await authUtils.navigateAfterAuth(destination);
  },
  // Navigate into the app after an explicit sign-in completes. Guards against a
  // redundant push when a page-level auth listener already moved us there, and
  // swallows the router's duplicate-navigation rejection.
  // `destination` is an explicit ?redirect= or null. Null resolves through
  // landingRoute(), which is why this is async: the default depends on a flag
  // whose per-user value only lands once the signed-in uid's allowlist listener
  // reports, and that cannot have happened before sign-in completed.
  navigateAfterAuth: async (destination) => {
    const target = destination || (await landingRoute());
    if (router.currentRoute.value.fullPath === target) return;
    router.push(target).catch(() => {});
  },
  // Completes the web signInWithRedirect flow at app startup. Gated on the
  // pending-redirect flag so it only fires after an explicit login (never a
  // passive visit), which is what lets it safely run globally — including on
  // Blog/FAQ, which have no per-page auth listener of their own.
  completePendingRedirectAuth: async () => {
    const stashed = sessionStorage.getItem(PENDING_AUTH_REDIRECT_KEY);
    if (!stashed) return;
    sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
    try {
      const result = await getRedirectResult(getAuth());
      // Seeded before anything else is awaited. A null result (the redirect
      // was cancelled) or a rejection seeds nobody: currentUser may then be
      // the account signed in before the redirect, and seeding it would
      // attach this attempt's first touch to that account for good.
      issueSeed(result?.user);
    } catch (error) {
      console.error("[auth] redirect result error", error);
    }
    await authUtils.waitForAuthInitialization();
    if (authStore.getUserId()) {
      // The marker means no explicit ?redirect= was set, so the default is
      // resolved HERE, after waitForAuthInitialization has put the signed-in
      // uid in the store. That ordering is the whole point of the marker: the
      // flag store keys its allowlist listener on that uid.
      const destination =
        stashed === DEFAULT_LANDING_MARKER ? null : stashed;
      await authUtils.navigateAfterAuth(destination);
    }
  },
  checkForRedirectAuth: (router) => {
    const auth = getAuth();
    if (authUtils.isMobile() && !isCapacitor()) {
      console.debug(
        "User is on a mobile device - checking if redirect auth exists"
      );
      getRedirectResult(auth).then((result) => {
        if (result !== null && result.user) {
          // Defensive only: on a successful redirect Auth has signed the user
          // in before any listener runs, so setupAuth never reaches this
          // signed-out branch and completePendingRedirectAuth reads the result.
          issueSeed(result.user);
          authStore.setUserId(result.user.uid);
          // getIdToken() is a Promise. Storing it unresolved put a Promise
          // object in the Authorization header of every backend call for the
          // rest of the session (every other call site in this file awaits it).
          result.user
            .getIdToken()
            .then((token) => authStore.setIdToken(token))
            .catch((error) =>
              console.error("[auth] could not read the redirect id token", error)
            );
          // Resolved here rather than at module load: the uid landed one line
          // above, and the landing decision waits on that uid's flag entry.
          pushWhenStillWaiting(router);
        } else {
          console.log("no auth redirect result");
        }
      });
    } else {
      console.debug("User is not on a mobile device - not checking auth yet");
    }
  },
  isMobile: () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  },
  // First-sign-in profile seed. It is called (through issueSeed) by the code
  // that completes each explicit sign-in, right after the sign-in call
  // resolves: signInWithProvider's popup and native branches, signInWithEmail,
  // completePendingRedirectAuth and checkForRedirectAuth. By then Auth has run
  // every onIdTokenChanged observer, the database's included, so the new
  // user's token is on the socket ahead of this write. The page-level
  // listeners (setupAuth, setupAuthPresence) used to call it and no longer
  // do: on a first visit to `/`, HomeView's listener ran before the
  // database's and sent the seed as a signed-out write, which the rules
  // refused. The only other caller is recoverInterruptedSeed.
  // Create-if-absent inside a transaction, so overlapping calls are safe.
  // The previous get()-then-set() pair read the node, then wrote the whole
  // node: anything written between the two calls (the onboarding wizard, the
  // backend newUser handler) was silently overwritten by the seed.
  seedUserProfileIfMissing: (user) => {
    // Already handled this uid in this session; the node either exists or the
    // seed committed or is still in flight.
    if (seededUids.has(user.uid)) return Promise.resolve();
    seededUids.add(user.uid);
    // The first tagged touch this browser or device saw, already normalized to
    // the RTDB rule on users/$uid/acquisition, or null. Read synchronously and
    // never awaited: the transaction below must be issued in the same tick as
    // the sign-in completion that called this. Any await here lets
    // DashboardView's timezone update() create the node first, the
    // create-if-absent transaction then aborts, and the account loses its seed.
    const now = Date.now();
    const acquisition = readAcquisitionForSeed(now);
    // Written before the transaction is issued, so a reload or closed tab at
    // any point after this leaves a record the next page load can finish.
    writePendingSeedMarker(user.uid, now);
    const db = getDatabase();
    return runTransaction(
      ref(db, "users/" + user.uid),
      (current) => {
        // Whatever is already there wins. Returning undefined aborts the
        // transaction without writing anything.
        if (current !== null) return undefined;
        const seed = {
          state: "Signed Up Only",
          signupPlatform: resolveSignupPlatform(),
        };
        // The rule only accepts acquisition inside the write that creates the
        // node, so this seed is the one chance to record it.
        if (acquisition) seed.acquisition = acquisition;
        return seed;
      },
      // Never apply the optimistic local write. On a cold cache the updater
      // runs against null first, and a locally-applied seed would momentarily
      // replace an existing profile for every users/ listener (DashboardView,
      // FreeChatView) before the server rejects it and it rolls back.
      { applyLocally: false }
    )
      // Callers proceed either way, so a rejected seed would otherwise strand
      // the account in the app with no profile and no trace. Release the
      // once-per-uid guard so the next explicit sign-in of this account in
      // this session tries again.
      .catch((error) => {
        seededUids.delete(user.uid);
        console.error("Could not seed the user profile", error);
      })
      // Settled: committed, aborted because the node exists, or refused.
      // Nothing is left to recover, and leaving the marker would re-run a
      // refused seed on every page load for a day.
      .finally(() => clearPendingSeedMarkerFor(user.uid));
  },
  // Hydrates authStore and navigates a signed-in visitor off a marketing page.
  // It does not seed the profile: the sign-in call sites do that (see
  // seedUserProfileIfMissing). It only finishes a seed a reload interrupted.
  setupAuth: (router, redirectPath) => {
    const auth = getAuth();
    recoverInterruptedSeed(auth);
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      if (user) {
        authStore.setUserId(user.uid);
        user.getIdToken().then((token) => {
          console.debug("token updated");
          authStore.setIdToken(token);
        });

        // Redirect to the intended destination, or resolve the default. The
        // uid was written to the store two statements above, so the landing
        // decision is asking about the account that just signed in.
        if (redirectPath) {
          router.push(redirectPath);
        } else {
          pushWhenStillWaiting(router);
        }
      } else {
        authUtils.checkForRedirectAuth(router);
      }
    });

    return unsubscribe;
  },
  // Non-redirecting variant of setupAuth for public pages that signed-in
  // users must be able to read (the legal pages the paywall links to, per
  // App Store Guideline 3.1.2). Hydrates authStore so AppNavigation shows
  // the signed-in state on a direct load, but never navigates. Like setupAuth,
  // it does not seed the profile (the sign-in call sites do) and only finishes
  // a seed a reload interrupted.
  setupAuthPresence: () => {
    const auth = getAuth();
    recoverInterruptedSeed(auth);
    return onIdTokenChanged(auth, (user) => {
      if (!user) {
        // Keep the store honest if auth dies while the page is open (token
        // revoked, session expired). signOut() also clears these, so this
        // only matters for the non-interactive paths.
        authStore.setUserId(null);
        authStore.setIdToken(null);
        return;
      }
      authStore.setUserId(user.uid);
      user.getIdToken().then((token) => authStore.setIdToken(token));
    });
  },
};
